import { ConfirmationResult, RecaptchaVerifier } from "firebase/auth";
import { useEffect, useRef, useState } from "react";
import { authManager } from "../../data/managers/AuthManager";
import { useAuth } from "../../data/providers/AuthProvider";
import { authService } from "../../firebase-config";
import { normalizePhone } from "../../utils/StringTool";

declare global {
  interface Window {
    recaptchaVerifier?: RecaptchaVerifier;
  }
}

export function useLogin({ country }: any) {
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState("input");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmittingWhatsapp, setIsSubmittingWhatsapp] = useState(false);
  const [isLogged, setLogged] = useState(false);
  const recaptchaContainerRef = useRef(null);
  const [confirmationResult, setConfirmationResult] =
    useState<ConfirmationResult | null>(null);

  const { user } = useAuth();

  const isPhoneValid = phone.length >= 7 && /^[0-9]+$/.test(phone);
  const isCodeValid = code.length >= 4;

  useEffect(() => {
    setLogged(!!user);
  }, []);

  useEffect(() => {
    if (!recaptchaContainerRef.current) return;
    if (!window.recaptchaVerifier) {
      window.recaptchaVerifier = new RecaptchaVerifier(
        authService,
        "recaptcha-container",
        {
          size: "invisible",
          callback: () => {
            (window.recaptchaVerifier as any)?.recaptcha?.reset();
          },
          "expired-callback": () => {
            (window.recaptchaVerifier as any)?.recaptcha?.reset();
          },
          "error-callback": () => {
            (window.recaptchaVerifier as any)?.recaptcha?.reset();
          },
        }
      );
    }

    return () => {
      if (window.recaptchaVerifier) {
        try {
          window.recaptchaVerifier.clear();
        } catch (e) {}
        delete window.recaptchaVerifier;
      }
    };
  }, []);

  const handleSendCodeBySms = async (e?: any) => {
    e?.preventDefault();
    if (!isPhoneValid) return;
    setIsSubmitting(true);
    setError("");
    try {
      const appVerifier = window.recaptchaVerifier;

      if (!appVerifier) {
        throw new Error(
          "Cannot use invisible captcha. Please reload the page."
        );
      }
      const result = await authManager.sendSmsCode(
        `${country.code}${phone}`,
        appVerifier
      );
      setConfirmationResult(result!);
      setStep("code");
    } catch (error) {
      console.log(error);
      setError("Error enviando código SMS");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyCodeSms = async (e: any) => {
    e.preventDefault();
    if (!isCodeValid) return;
    setIsSubmitting(true);
    setError("");
    try {
      await authManager.loginWithPhone(confirmationResult!, code);
      setLogged(true);
    } catch (error) {
      setError("Código incorrecto");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSendCodeByWhatsapp = async (e: any) => {
    e?.preventDefault();
    if (!isPhoneValid) return;
    setIsSubmittingWhatsapp(true);
    setError("");
    try {
      const normalized = normalizePhone(country.code, phone);
      await authManager.sendWhatsappCode(normalized);
      setStep("codeWhatsapp");
    } catch (error) {
      console.log(error);
      setError("Error enviando código SMS");
    } finally {
      setIsSubmittingWhatsapp(false);
    }
  };

  const handleVerifyCodeByWhatsapp = async (e: any) => {
    e.preventDefault();
    if (!isCodeValid) return;
    setIsSubmittingWhatsapp(true);
    setError("");
    try {
      const normalized = normalizePhone(country.code, phone);
      await authManager.loginWithWhatsapp({
        phoneNumber: normalized,
        code,
      });
      setLogged(true);
    } catch (error) {
      setError("Código incorrecto");
    } finally {
      setIsSubmittingWhatsapp(false);
    }
  };

  return {
    phone,
    setPhone,
    code,
    setCode,
    step,
    setStep,
    error,
    setError,
    isSubmitting,
    setIsSubmitting,
    isSubmittingWhatsapp,
    setIsSubmittingWhatsapp,
    isLogged,
    setLogged,
    isPhoneValid,
    isCodeValid,
    handleSendCodeBySms,
    handleVerifyCodeSms,
    handleSendCodeByWhatsapp,
    handleVerifyCodeByWhatsapp,
    recaptchaContainerRef,
  };
}
